import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { userApi } from "../accounts/authuser";
import { paymentStatementsApi } from "./paymentStatements";
import { transactionsApi } from "./transactions";

/**
 * `POST /api/me/payment-requests` のリクエスト。
 *
 * `amount` 省略時は BE が `balance_settled` 全額を申請額とする。
 * 引き出せる上限は `balance_settled`（新仕様 4 バケットの確定済み受取金）であり、
 * `free` や `available_for_withdrawal` ではない点に注意（payment-requests.md §1）。
 */
export interface CreatePaymentRequestParams {
  /** 引き出し額（円・整数）。下限 ¥100、上限は `balance_settled`。 */
  amount?: number;
}

export interface CreatePaymentRequestResponse {
  status: string;
  data: {
    payment_request: {
      id: number;
      payment_code: string;
      amount: string;
      /** 1=requested / 2=processing（本 API は常に 2）/ 3=paid / 4=rejected / 5=canceled */
      status_type: number;
      created: string;
    };
    ledger: {
      id: string;
      event_type: string;
      idempotency_key: string;
    };
    balance_after: {
      settled: string;
    };
  };
}

export const paymentRequestsApi = createApi({
  reducerPath: "paymentRequestsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    createPaymentRequest: builder.mutation<
      CreatePaymentRequestResponse,
      CreatePaymentRequestParams
    >({
      query: ({ amount }) => ({
        url: "/api/me/payment-requests",
        method: "POST",
        body: amount !== undefined ? { amount } : {},
      }),
      // 申請成功で balance_settled が減り、本人の支払明細一覧にも行が増える。
      // 振込申請対象（withdrawable-transactions）も紐付け済みになり対象から外れるため、
      // Transactions タグを無効化してグローバルメニューの金額・リストを再取得させる。
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(userApi.util.invalidateTags(["CurrentUser"]));
          dispatch(
            paymentStatementsApi.util.invalidateTags([
              { type: "PaymentStatements", id: "ME_LIST" },
            ]),
          );
          dispatch(transactionsApi.util.invalidateTags(["Transactions"]));
        } catch {
          // 失敗時はキャッシュを変更しない（次回 fetch で整合）
        }
      },
    }),
  }),
});

export const { useCreatePaymentRequestMutation } = paymentRequestsApi;
